import { X, User, Shield, Plug, Rocket } from "lucide-react";
import api from "@/lib/api";

export default function AdminUserDrawer({
  detail,
  onClose,
  setPaidStrategyAccess,
  toggleActive,
  approveUser,
}) {
  if (!detail?.user) return null;

  const paidAccess = !!detail.user.special_strategy_access;
  const isAdmin = detail.user.role === "admin";

  return (
    <div className="h-full w-full bg-white border border-slate-200 shadow-sm overflow-y-auto">
      <div className="w-full max-w-full bg-white h-full shadow-2xl overflow-y-auto">

        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">{detail.user.name}</h2>
            <p className="text-sm text-slate-500">{detail.user.email}</p>
          </div>

          <button onClick={onClose}>
            <X />
          </button>
        </div>

        <div className="p-6 space-y-6">

          <div className="grid grid-cols-2 gap-3">

            <div className="border rounded-xl p-4">
              <User className="mb-2" />
              <div className="text-xs text-slate-500">Role</div>
              <div className="font-semibold">{detail.user.role}</div>
            </div>

            <div className="border rounded-xl p-4">
              <Shield className="mb-2" />
              <div className="text-xs text-slate-500">Status</div>
              <div className="font-semibold">
                {detail.user.status === "pending"
                  ? "Pending Approval"
                  : (detail.user.disabled || ["disabled", "inactive"].includes(detail.user.status))
                    ? "Disabled"
                    : "Active"}
              </div>
            </div>

            <div className="border rounded-xl p-4">
              <Plug className="mb-2" />
              <div className="text-xs text-slate-500">Brokers</div>
              <div className="font-semibold">
                {detail.brokers?.length || 0}
              </div>
            </div>

            <div className="border rounded-xl p-4">
              <Rocket className="mb-2" />
              <div className="text-xs text-slate-500">Deployments</div>
              <div className="font-semibold">
                {detail.deployments?.length || 0}
              </div>
            </div>

          </div>

          {/* PAID STRATEGY ACCESS */}
          {!isAdmin && (
            <div
              className="p-4 rounded-xl border border-amber-200 bg-amber-50"
              data-testid="paid-strategy-access"
            >
              <div className="flex items-center justify-between gap-4">

                <div>
                  <div className="font-semibold text-slate-800">
                    ⭐ Paid Strategy Access
                  </div>

                  <div className="text-xs text-slate-500 mt-1">
                    Controls access to proprietary paid strategies.
                  </div>
                </div>

                <div className="flex items-center gap-3">

                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                      paidAccess
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {paidAccess ? "ENABLED" : "DISABLED"}
                  </span>

                  <button
                    type="button"
                    data-testid="toggle-paid-strategy-access"
                    onClick={() =>
                      setPaidStrategyAccess(
                        detail.user.id,
                        !paidAccess
                      )
                    }
                    className={`px-3 py-2 rounded-md text-xs font-semibold ${
                      paidAccess
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-slate-900 text-white hover:bg-slate-800"
                    }`}
                  >
                    {paidAccess ? "Disable" : "Enable"}
                  </button>

                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="font-semibold mb-3">Strategies</h3>

            {(detail.strategies || []).map((s, i) => (
              <div
                key={i}
                className="border rounded-lg p-3 flex justify-between mb-2"
              >
                <span>{s.name}</span>
                <span className="text-slate-500">
                  {s.underlying}
                </span>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t border-slate-200">
            <h3 className="font-semibold mb-3">Account Actions</h3>

            <div className="flex flex-wrap gap-2">

              {!isAdmin && detail.user.status === "pending" && (
                <button
                  type="button"
                  className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold"
                  onClick={() => approveUser(detail.user.id)}
                >
                  Approve Account
                </button>
              )}

              {!isAdmin && detail.user.status !== "pending" && (
                <button
                  type="button"
                  className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold"
                  onClick={() => toggleActive(detail.user.id)}
                >
                  {detail.user.disabled ? "Enable Account" : "Disable Account"}
                </button>
              )}

              <button
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold"
                onClick={async () => {
                  const pwd = prompt("Enter new password");
                  if (!pwd) return;

                  await api.patch(
                    `/admin/users/${detail.user.id}/password`,
                    { password: pwd }
                  );

                  alert("Password updated.");
                }}
              >
                Reset Password
              </button>

              <button
                className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold"
                onClick={async () => {
                  const role =
                    detail.user.role === "admin"
                      ? "user"
                      : "admin";

                  await api.patch(
                    `/admin/users/${detail.user.id}`,
                    { role }
                  );

                  alert(
                    role === "admin"
                      ? "User promoted to Admin."
                      : "User changed to User."
                  );

                  window.location.reload();
                }}
              >
                {detail.user.role === "admin"
                  ? "Make User"
                  : "Make Admin"}
              </button>

              <button
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold"
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Delete this user permanently?"
                    )
                  ) return;

                  await api.delete(
                    `/admin/users/${detail.user.id}`
                  );

                  alert("User deleted.");
                  onClose();
                  window.location.reload();
                }}
              >
                Delete User
              </button>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
